// Toast notifications — dependency-free. Theme tokens uyumlu.
// Kullanım:
//   1) App.js'in en üstüne <ToastProvider> ile sarmala
//   2) Herhangi bir komponente: import { toast } from '../components/Toast';
//      toast.success('Kaydedildi'); toast.error('Hata'); toast.info('...'); toast.warning('...');
//   3) Veya hook ile: const t = useToast(); t.success('...');
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import i18n from '../i18n';
import { translateMessage } from '../i18n/messageMap';

const ToastContext = createContext(null);

// Mesaj çevirici — string ise messageMap'ten, JSX ise aynen
function xlate(msg) {
  return typeof msg === 'string' ? translateMessage(msg, i18n.language) : msg;
}

// Global publish function — modül scope'unda, Provider mount olunca set edilir.
let publish = null;

function makeId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

export function ToastProvider({ children, position = 'top-right', defaultDuration = 3500 }) {
  const [items, setItems] = useState([]);

  const push = useCallback((kind, message, opts = {}) => {
    const id = makeId();
    const duration = opts.duration ?? defaultDuration;
    setItems(prev => [...prev, { id, kind, message: xlate(message), duration, action: opts.action }]);
    if (duration > 0) {
      setTimeout(() => {
        setItems(prev => prev.filter(t => t.id !== id));
      }, duration);
    }
    return id;
  }, [defaultDuration]);

  const dismiss = useCallback((id) => {
    setItems(prev => prev.filter(t => t.id !== id));
  }, []);

  useEffect(() => {
    publish = push;
    return () => { publish = null; };
  }, [push]);

  const value = {
    success: (msg, opts) => push('success', msg, opts),
    error:   (msg, opts) => push('error',   msg, opts),
    info:    (msg, opts) => push('info',    msg, opts),
    warning: (msg, opts) => push('warning', msg, opts),
    dismiss,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer items={items} onDismiss={dismiss} position={position} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

// Module-level shorthand (Provider mount sonrası kullanılabilir).
export const toast = {
  success: (msg, opts) => publish && publish('success', msg, opts),
  error:   (msg, opts) => publish && publish('error',   msg, opts),
  info:    (msg, opts) => publish && publish('info',    msg, opts),
  warning: (msg, opts) => publish && publish('warning', msg, opts),
};

// ─────────────────────────────────────────────────────────────────────────
// UI

function ToastContainer({ items, onDismiss, position }) {
  const posStyle = POSITIONS[position] || POSITIONS['top-right'];
  return (
    <div style={{ ...S.container, ...posStyle }} aria-live="polite" aria-atomic="true">
      {items.map(t => (
        <ToastItem key={t.id} item={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({ item, onDismiss }) {
  const cfg = KINDS[item.kind] || KINDS.info;
  return (
    <div style={{
      ...S.toast,
      borderLeft: `4px solid ${cfg.color}`,
      animation: 'toast-in 0.18s ease-out',
    }}>
      <span style={{ ...S.icon, color: cfg.color }} aria-hidden="true">{cfg.icon}</span>
      <span style={S.message}>{item.message}</span>
      {item.action && (
        <button
          type="button"
          onClick={() => { item.action.onClick(); onDismiss(item.id); }}
          style={{ ...S.actionBtn, color: cfg.color }}
        >
          {item.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={() => onDismiss(item.id)}
        style={S.closeBtn}
        aria-label="Kapat"
      >×</button>
    </div>
  );
}

const KINDS = {
  success: { color: 'var(--success)', icon: '✓' },
  error:   { color: 'var(--danger)',  icon: '✕' },
  warning: { color: 'var(--warning)', icon: '!' },
  info:    { color: 'var(--info)',    icon: 'i' },
};

const POSITIONS = {
  'top-right':    { top: 16, right: 16, alignItems: 'flex-end' },
  'top-left':     { top: 16, left:  16, alignItems: 'flex-start' },
  'bottom-right': { bottom: 16, right: 16, alignItems: 'flex-end' },
  'bottom-left':  { bottom: 16, left:  16, alignItems: 'flex-start' },
  'top-center':   { top: 16, left: '50%', transform: 'translateX(-50%)', alignItems: 'center' },
};

const S = {
  container: {
    position: 'fixed',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    pointerEvents: 'none',
  },
  toast: {
    pointerEvents: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 280,
    maxWidth: 480,
    padding: '10px 12px',
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
    boxShadow: 'var(--shadow-lg)',
    fontSize: 'var(--text-sm)',
    color: 'var(--text-primary)',
  },
  icon: {
    width: 22, height: 22,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    fontSize: 13,
    background: 'var(--bg-tertiary)',
    flexShrink: 0,
  },
  message: { flex: 1, lineHeight: 1.4 },
  actionBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 'var(--text-xs)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    padding: '2px 6px',
  },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--text-tertiary)',
    fontSize: 20,
    lineHeight: 1,
    padding: '0 4px',
    flexShrink: 0,
  },
};

if (typeof document !== 'undefined' && !document.getElementById('toast-keyframes')) {
  const s = document.createElement('style');
  s.id = 'toast-keyframes';
  s.textContent = `
    @keyframes toast-in {
      from { transform: translateY(-8px); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }
  `;
  document.head.appendChild(s);
}
